import React from 'react';
import { motion } from 'framer-motion';

// Reusable Card Component for consistent styling
const ServiceCard = ({ children, bgColor, textColor = 'text-black', className = '' }) => {
    return (
        <motion.div
            className={`${bgColor} overflow-hidden min-h-[250px] md:min-h-[280px] lg:min-h-[320px] ${className}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
        >
            <div className={`p-6 md:p-8 lg:p-10 h-full flex flex-col justify-center ${textColor}`}>
                {children}
            </div>
        </motion.div>
    );
};

const Services = () => {
    return (
        <motion.div
            id="services"
            className="w-full relative"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
        >
            {/* SECTION 1: Orange Box - Header */}
            <div
                id='Services-Orange-Box'
                className='bg-[#ff6e19] w-full py-24 md:py-32 lg:py-40 flex justify-center items-center'
            >
                <div className='flex flex-col items-center'>
                    {/* Services Label */}
                    <div className='font-nunito text-xs sm:text-sm lg:text-base font-medium text-black/90'>
                        SERVICES
                    </div>

                    {/* Main Heading - Responsive sizing */}
                    <div className='text-center mt-6 md:mt-8 lg:mt-10'>
                        <div className='font-cormorant font-semibold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-black'>
                            How Can I Help You?
                        </div>
                        <div className='font-cormorant font-semibold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-black mt-2'>
                            Explore My Expertise
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 2: Service Cards Grid */}
            <div className='bg-[#b79347]'>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0'>

                    {/* Row 1, Card 1: General Counselling */}
                    <ServiceCard
                        bgColor="bg-white"
                        className='flex justify-start sm:justify-center md:justify-start items-center'
                    >
                        <div className='flex flex-col gap-y-1 md:gap-y-2 w-full text-left sm:text-center md:text-left pl-8 sm:pl-0 md:pl-12'>
                            <h3 className="font-cormorant font-semibold text-3xl sm:text-3xl lg:text-3xl xl:text-4xl">
                                General Counselling
                            </h3>
                            <p className="font-nunito text-sm sm:text-sm lg:text-sm xl:text-base font-light">
                                Explore your thoughts and feelings
                            </p>
                        </div>
                    </ServiceCard>

                    {/* Row 1, Card 2: Arrow graphic */}
                    <ServiceCard
                        bgColor="bg-[#e3daed]"
                        className='flex justify-center items-center'
                    >
                        <img src="arrow-card.png" alt="Service decoration" className='w-full h-full object-cover object-center' />
                    </ServiceCard>

                    {/* Row 1, Card 3: Clinical Therapy */}
                    <ServiceCard
                        bgColor="bg-[#ffffff]"
                        className='flex justify-start sm:justify-center md:justify-start items-center'
                    >
                        <div className='flex flex-col gap-y-1 md:gap-y-2 w-full text-left sm:text-center md:text-left pl-8 sm:pl-0 md:pl-12'>
                            <h3 className="font-cormorant font-semibold text-3xl sm:text-3xl lg:text-3xl xl:text-4xl">
                                Clinical Therapy
                            </h3>
                            <p className="font-nunito text-sm sm:text-sm lg:text-sm xl:text-base font-light">
                                Find your inner strength
                            </p>
                        </div>
                    </ServiceCard>

                    {/* Card 4: Circles decoration (swapped from Row 3) */}
                    <ServiceCard
                        bgColor="bg-[#e3daed]"
                        className='flex justify-center items-center'
                    >
                        <img src="circles-box-card.png" alt="Service decoration" className='w-full h-full object-cover object-center' />
                    </ServiceCard>

                    {/* Card 5: Couple & Family Therapy */}
                    <ServiceCard
                        bgColor="bg-white"
                        className='flex justify-start sm:justify-center md:justify-start items-center'
                    >
                        <div className='flex flex-col gap-y-1 md:gap-y-2 w-full text-left sm:text-center md:text-left pl-8 sm:pl-0 md:pl-12'>
                            <h3 className="font-cormorant font-semibold text-3xl sm:text-3xl lg:text-3xl xl:text-4xl">
                                Couple & Family Therapy
                            </h3>
                            <p className="font-nunito text-sm sm:text-sm lg:text-sm xl:text-base font-light">
                                Build stronger, healthier connections
                            </p>
                        </div>
                    </ServiceCard>

                    {/* Card 6: Right box decoration */}
                    <ServiceCard
                        bgColor="bg-[#cca44f]"
                        className='flex justify-center items-center'
                    >
                        <img src="right-box-card.png" alt="Service decoration" className='w-full h-full object-cover object-center' />
                    </ServiceCard>

                    {/* Row 3, Card 7: Mentorship sessions */}
                    <ServiceCard
                        bgColor="bg-white"
                        className='flex justify-start sm:justify-center md:justify-start items-center'
                    >
                        <div className='flex flex-col gap-y-1 md:gap-y-2 w-full text-left sm:text-center md:text-left pl-8 sm:pl-0 md:pl-12'>
                            <h3 className="font-cormorant font-semibold text-3xl sm:text-3xl lg:text-3xl xl:text-4xl">
                                Mentorship Sessions
                            </h3>
                            <p className="font-nunito text-sm sm:text-sm lg:text-sm xl:text-base font-light">
                                Guidance and support for your journey
                            </p>
                        </div>
                    </ServiceCard>

                    {/* Row 3, Card 8: Center card image (swapped from Row 2) */}
                    <ServiceCard
                        bgColor="bg-[#ff6c19]"
                        className='flex justify-center items-center'
                    >
                        <img src="center-card.png" alt="Service decoration" className='w-full h-full object-cover object-center' />
                    </ServiceCard>

                    {/* Row 3, Card 9: Discovery Consultation */}
                    <ServiceCard
                        bgColor="bg-white"
                        className='flex justify-start sm:justify-center md:justify-start items-center'
                    >
                        <div className='flex flex-col gap-y-1 md:gap-y-2 w-full text-left sm:text-center md:text-left pl-8 sm:pl-0 md:pl-12'>
                            <h3 className="font-cormorant font-semibold text-3xl sm:text-3xl lg:text-3xl xl:text-4xl">
                                Discovery Consultation
                            </h3>
                            <p className="font-nunito text-sm sm:text-sm lg:text-sm xl:text-base font-light">
                                Let's Start Your Journey Together
                            </p>
                        </div>
                    </ServiceCard>

                </div>
            </div>
        </motion.div>
    );
};

export default Services;
